import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { Tables } from "@/integrations/supabase/types";

type TrainingGroup = Tables<"training_groups">;

const scheduleRowSchema = z.object({
  day_of_week: z.number().min(0).max(6),
  start_time: z.string().min(1),
  duration_minutes: z.number().min(15),
  court_id: z.string().nullable(),
});

const groupSchema = z.object({
  name: z.string().min(1),
  age_category: z.string().min(1),
  level: z.string().min(1),
  coach_id: z.string().nullable(),
  max_players: z.number().min(1).max(50),
  color: z.string(),
  schedule: z.array(scheduleRowSchema).min(1),
});

type GroupFormValues = z.infer<typeof groupSchema>;

const AGE_OPTION_KEYS = [
  { value: "junior_u10", key: "ageU10" },
  { value: "junior_u12", key: "ageU12" },
  { value: "junior_u14", key: "ageU14" },
  { value: "junior_u16", key: "ageU16" },
  { value: "junior_u18", key: "ageU18" },
  { value: "adult", key: "ageAdult" },
  { value: "senior", key: "ageSenior" },
];

const LEVEL_OPTION_KEYS = [
  { value: "beginner", key: "levelBeginner" },
  { value: "intermediate", key: "levelIntermediate" },
  { value: "advanced", key: "levelAdvanced" },
  { value: "elite", key: "levelElite" },
];

const DAY_OPTION_KEYS = [
  { value: 1, key: "dayMonday" },
  { value: 2, key: "dayTuesday" },
  { value: 3, key: "dayWednesday" },
  { value: 4, key: "dayThursday" },
  { value: 5, key: "dayFriday" },
  { value: 6, key: "daySaturday" },
  { value: 0, key: "daySunday" },
];

export default function GroupForm({
  group,
  seasonId,
  onBack,
  onSaved,
}: {
  group: TrainingGroup | null;
  seasonId: string;
  onBack: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { courts } = useOrgHall();

  // Fetch coaches: users with coach or admin role in org
  const { data: coaches = [] } = useQuery({
    queryKey: ["org-coaches", organization?.id],
    queryFn: async () => {
      // Get coach/admin user IDs from user_roles (org-scoped)
      const { data: roles } = await supabase
        .from("user_roles")
        .select("user_id")
        .in("role", ["coach", "admin"])
        .eq("organization_id", organization!.id);
      const roleUserIds = (roles ?? []).map((r) => r.user_id);
      if (roleUserIds.length === 0) return [];

      // Fetch profiles separately (no FK join — FK points to auth.users, not profiles)
      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name")
        .in("id", [...new Set(roleUserIds)]);

      return (profiles ?? []).map((p) => ({
        id: p.id,
        name: p.full_name || p.id.slice(0, 8),
      }));
    },
    enabled: !!organization?.id,
  });

  const existingSchedule = group?.schedule
    ? (group.schedule as any[]).map((s) => ({
        day_of_week: s.day_of_week ?? 1,
        start_time: s.start_time ?? "16:00",
        duration_minutes: s.duration_minutes ?? 60,
        court_id: s.court_id ?? null,
      }))
    : [{ day_of_week: 1, start_time: "16:00", duration_minutes: 60, court_id: null }];

  const form = useForm<GroupFormValues>({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      name: group?.name ?? "",
      age_category: group?.age_category ?? "",
      level: group?.level ?? "",
      coach_id: group?.coach_id ?? null,
      max_players: group?.max_players ?? 8,
      color: group?.color ?? "#3b82f6",
      schedule: existingSchedule,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "schedule",
  });

  const saveMutation = useMutation({
    mutationFn: async (values: GroupFormValues) => {
      const payload = {
        name: values.name,
        age_category: values.age_category,
        level: values.level,
        coach_id: values.coach_id || null,
        max_players: values.max_players,
        color: values.color,
        schedule: values.schedule,
      };
      if (group) {
        const { error } = await supabase
          .from("training_groups")
          .update(payload)
          .eq("id", group.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("training_groups").insert([{
          ...payload,
          season_id: seasonId,
          organization_id: organization!.id,
        }]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(group ? t("toast.groupUpdated") : t("toast.groupCreated"));
      onSaved();
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("toast.couldNotSaveGroup")); },
  });

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 gap-1">
        <ArrowLeft className="h-4 w-4" /> {t("seasons.groups.backToGroups")}
      </Button>
      <h2 className="text-lg font-semibold mb-4">
        {group ? t("seasons.groups.editGroup") : t("seasons.groups.createGroup")}
      </h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => saveMutation.mutate(v))} className="space-y-4 max-w-lg">
          <FormField control={form.control} name="name" render={({ field }) => (
            <FormItem>
              <FormLabel>{t("seasons.groups.name")}</FormLabel>
              <FormControl><Input placeholder={t("seasons.groups.namePlaceholder")} {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />

          <div className="grid grid-cols-2 gap-4">
            <FormField control={form.control} name="age_category" render={({ field }) => (
              <FormItem>
                <FormLabel>{t("seasons.groups.ageCategory")}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder={t("seasons.groups.selectPlaceholder")} /></SelectTrigger></FormControl>
                  <SelectContent>
                    {AGE_OPTION_KEYS.map((o) => <SelectItem key={o.value} value={o.value}>{t(`seasons.groups.${o.key}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="level" render={({ field }) => (
              <FormItem>
                <FormLabel>{t("seasons.groups.level")}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder={t("seasons.groups.selectPlaceholder")} /></SelectTrigger></FormControl>
                  <SelectContent>
                    {LEVEL_OPTION_KEYS.map((o) => <SelectItem key={o.value} value={o.value}>{t(`seasons.groups.${o.key}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField control={form.control} name="coach_id" render={({ field }) => (
              <FormItem>
                <FormLabel>{t("seasons.groups.coach")}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value ?? undefined}>
                  <FormControl><SelectTrigger><SelectValue placeholder={t("seasons.groups.noneSelected")} /></SelectTrigger></FormControl>
                  <SelectContent>
                    {coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="max_players" render={({ field }) => (
              <FormItem>
                <FormLabel>{t("seasons.groups.maxPlayers")}</FormLabel>
                <FormControl>
                  <NumberInput
                    min={1}
                    max={50}
                    value={field.value}
                    onValueChange={(v) => field.onChange(v)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
          </div>

          <FormField control={form.control} name="color" render={({ field }) => (
            <FormItem>
              <FormLabel>{t("seasons.groups.color")}</FormLabel>
              <FormControl>
                <Input type="color" className="w-16 h-10 p-1 cursor-pointer" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />

          {/* Schedule */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <FormLabel>{t("seasons.groups.weeklySchedule")}</FormLabel>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ day_of_week: 1, start_time: "16:00", duration_minutes: 60, court_id: null })}
                className="gap-1"
              >
                <Plus className="h-3.5 w-3.5" /> {t("seasons.groups.addSchedule")}
              </Button>
            </div>
            {form.formState.errors.schedule?.root && (
              <p className="text-sm font-medium text-destructive">{form.formState.errors.schedule.root.message}</p>
            )}
            {fields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-[1fr_0.7fr_0.6fr_1fr_auto] gap-2 items-end">
                <FormField control={form.control} name={`schedule.${index}.day_of_week`} render={({ field: f }) => (
                  <FormItem>
                    {index === 0 && <FormLabel className="text-xs">{t("seasons.groups.day")}</FormLabel>}
                    <Select onValueChange={(v) => f.onChange(parseInt(v))} defaultValue={String(f.value)}>
                      <FormControl><SelectTrigger className="text-sm"><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {DAY_OPTION_KEYS.map((d) => <SelectItem key={d.value} value={String(d.value)}>{t(`seasons.groups.${d.key}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
                <FormField control={form.control} name={`schedule.${index}.start_time`} render={({ field: f }) => (
                  <FormItem>
                    {index === 0 && <FormLabel className="text-xs">{t("seasons.groups.time")}</FormLabel>}
                    <FormControl><Input type="time" className="text-sm" {...f} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name={`schedule.${index}.duration_minutes`} render={({ field: f }) => (
                  <FormItem>
                    {index === 0 && <FormLabel className="text-xs">{t("seasons.groups.minutes")}</FormLabel>}
                    <FormControl>
                      <NumberInput
                        min={15}
                        max={180}
                        className="text-sm"
                        value={f.value}
                        onValueChange={(v) => f.onChange(v)}
                      />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name={`schedule.${index}.court_id`} render={({ field: f }) => (
                  <FormItem>
                    {index === 0 && <FormLabel className="text-xs">{t("seasons.groups.court")}</FormLabel>}
                    <Select onValueChange={f.onChange} defaultValue={f.value ?? undefined}>
                      <FormControl><SelectTrigger className="text-sm"><SelectValue placeholder={t("seasons.groups.optional")} /></SelectTrigger></FormControl>
                      <SelectContent>
                        {courts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={() => remove(index)}
                  disabled={fields.length <= 1}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t("seasons.groups.saving") : group ? t("seasons.groups.update") : t("seasons.groups.createGroup")}
          </Button>
        </form>
      </Form>
    </div>
  );
}
